import { RecommendationsParams, SortValues, Technique } from '@omniafishing/core';
import React from 'react';
import { useFrontload } from 'react-frontload';
import { useDispatch, useSelector } from 'react-redux';
import { useAfterMountedEffect } from '../../../hooks/use_after_mounted_effect';
import { useQueryString } from '../../../hooks/use_query_string';
import { useRudderstack } from '../../../hooks/use_rudderstack';
import { OmniaQueryParams, OmniaSortQueryParams } from '../../../lib/query_string';
import {
  getIpCurrentSeasonGroup,
  getIpLatitude,
  getIpLongitude,
} from '../../../redux/geographic_location';
import {
  CATEGORY_PAGE_LIMIT_DEFAULT,
  fetchRecommendationFamilies,
  fetchRecommendationFamiliesFacets,
  getLoadingState,
  getProductFamilies,
  getProductFamiliesFacets,
  getProductFamiliesTotalResults,
  getRecommendationId,
} from '../../../redux/recommendations';
import { TechniqueProductsGrid, TechniqueProductsGridProps } from './technique_products_grid';

interface OwnProps {
  technique: Technique;
}

export const TechniqueProductsGridContainer = (props: OwnProps) => {
  const { technique } = props;
  const dispatch = useDispatch();
  const { anonymousId } = useRudderstack();
  const ipLat = useSelector(getIpLatitude);
  const ipLng = useSelector(getIpLongitude);
  const recommendation_id = useSelector(getRecommendationId);
  const defaultSortOrder = ipLat && ipLng ? SortValues.BEST_MATCH : SortValues.BESTSELLING;
  const ipSeasonGroup = useSelector(getIpCurrentSeasonGroup);

  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<{
    [OmniaQueryParams.pg]: number;
    [OmniaQueryParams.sort]: SortValues;
    [OmniaQueryParams.subcategories]: string[];
    [OmniaQueryParams.species]: string[];
    [OmniaQueryParams.brands]: string[];
    [OmniaSortQueryParams.osort_latitude]: number;
    [OmniaSortQueryParams.osort_longitude]: number;
    [OmniaSortQueryParams.osort_anonymous_id]: string;
    [OmniaSortQueryParams.osort_treatment]: string;
  }>();
  const page = currentQuery.pg || 1;
  const sortOrder = currentQuery.sort || defaultSortOrder;
  const selectedSpecies = currentQuery.species || [];
  const selectedBrands = currentQuery.brands || [];
  const selectedSubcategories = currentQuery.subcategories || [];

  const facetParams: RecommendationsParams = {
    [OmniaQueryParams.styles]: [technique.name],
    [OmniaQueryParams.species]: selectedSpecies,
    include_hidden_products: false,
  };

  useAfterMountedEffect(() => {
    dispatch(fetchRecommendationFamiliesFacets(facetParams));
  }, [selectedSpecies.join(), technique.name]);

  const recommendationsParams: RecommendationsParams = {
    ...facetParams,
    [OmniaQueryParams.brands]: selectedBrands,
    [OmniaQueryParams.subcategories]: selectedSubcategories,
    page: Number(page),
    sort: sortOrder,
    limit: CATEGORY_PAGE_LIMIT_DEFAULT,
    lat: Number(currentQuery[OmniaSortQueryParams.osort_latitude]) || ipLat,
    lng: Number(currentQuery[OmniaSortQueryParams.osort_longitude]) || ipLng,
    anonymous_id: currentQuery[OmniaSortQueryParams.osort_anonymous_id] || anonymousId,
    treatment: currentQuery[OmniaSortQueryParams.osort_treatment],
    user_season_group: ipSeasonGroup.name,
  };

  useAfterMountedEffect(() => {
    dispatch(fetchRecommendationFamilies(recommendationsParams));
  }, [
    selectedSpecies.join(),
    technique.name,
    selectedBrands.join(),
    selectedSubcategories.join(),
    page,
    sortOrder,
  ]);

  useFrontload(`TechniqueProductsGridContainer:${technique.name}`, async () => {
    await Promise.all([
      dispatch(fetchRecommendationFamilies(recommendationsParams)),
      dispatch(fetchRecommendationFamiliesFacets(facetParams)),
    ]);

    return true;
  });

  const propsPass: TechniqueProductsGridProps = {
    ...props,
    defaultSortOrder,
    loadingState: useSelector(getLoadingState),
    recommendation_id,
    recommendedFamilies: useSelector(getProductFamilies),
    recommendedFamiliesTotalResults: useSelector(getProductFamiliesTotalResults),
    recommendedFamiliesFacets: useSelector(getProductFamiliesFacets),
  };

  return <TechniqueProductsGrid {...propsPass} />;
};
